import { PropertyTypes } from "epsm-api-types";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
	formatValue,
	formatValueCurrency,
} from "@/components/widgets/helpers/FormattingHelpers";
import { useAuth } from "@/hooks/useAuth";
import { useGetStripeRoles } from "@/hooks/useGetUserRoles/useGetStripeRoles";
import { cn } from "@/lib/utils";
import { useDefineUserStatus } from "@/pages/DashboardPage/hooks/useDefineUserStatus";
import { getAuthService } from "@/services/user-auth/auth-factory";
import { EmailLink } from "@/shared/components/EmailLink";
import { PricingTable } from "@/shared/components/PricingTable";
import { authTextColor, SUPPORT_EMAIL } from "@/shared/constants/common";
import { useUserData } from "../../lib/hooks/useUserData";
import { useGetStripeBillingUrl } from "../hooks/useGetStripeBillingUrl";
import { useScrollIntoPriceTable } from "../hooks/useScrollIntoPriceTable";
import { RenderDialogForm } from "./components/RenderDialogForm";
import { UpdateAccountForm } from "./components/UpdateAccountForm";

const BUTTON_STYLE =
	"text-[16px] text-gold font-bold mt-4 w-[190px] h-fit bg-transparent justify-start p-0 hover:bg-transparent";

export const Account = () => {
	const [openModal, setOpenModal] = useState(false);
	const pricingRef = useRef<HTMLDivElement>(null);
	const stripeRoles = useGetStripeRoles();

	const isUserPremium = getAuthService(stripeRoles).isPremium;
	const [searchParams] = useSearchParams();
	const readyToUpgrade = !!searchParams.get("upgrade");

	const [showPricingTable, setShowPricingTable] =
		useState<boolean>(readyToUpgrade);

	const { authService } = useAuth();
	const { userData, updateUserData, isLoadingGet, isLoadingUpdate } =
		useUserData();

	const [subscriptionWillCancel, setSubscriptionWillCancel] = useState(
		userData?.subscriptionWillCancel ?? false,
	);

	const { billingUrl, errorFetchBillingUrl } =
		useGetStripeBillingUrl(isUserPremium);

	useDefineUserStatus();
	useScrollIntoPriceTable(pricingRef, readyToUpgrade);

	useEffect(() => {
		setShowPricingTable(readyToUpgrade || !isUserPremium);
	}, [isUserPremium, readyToUpgrade]);

	useEffect(() => {
		setSubscriptionWillCancel(userData?.subscriptionWillCancel || false);
	}, [userData]);

	const memberSince = formatValue(userData?.memberSince, PropertyTypes.DATE);

	const nextPaymentDate = formatValue(
		userData?.subscriptionExpiresAt,
		PropertyTypes.DATE,
	);

	const subscriptionTextColor = authService?.color
		? authTextColor[authService.color]
		: "";

	if (!userData || isLoadingGet) {
		return <Spinner />;
	}

	return (
		<div className="flex flex-col gap-10 w-full">
			<div className="flex flex-col gap-10 w-full px-5 sm:px-20 md:px-40 py-5">
				<div className="flex flex-col gap-10 w-full h-full max-w-3xl">
					<div>
						<h5 className="text-2xl font-bold">Account settings</h5>
						<Separator className="mt-3" />
					</div>

					<section className="flex flex-col gap-5" data-testid="user-section">
						<div>
							<h6 className="text-xl font-medium">User</h6>
							<Separator className="mt-3" />
						</div>

						<UpdateAccountForm
							userData={userData}
							updateUserData={updateUserData}
							isLoadingUpdate={isLoadingUpdate}
						/>
					</section>

					<section
						className="flex flex-col gap-5"
						data-testid="subscription-section"
					>
						<div>
							<h6
								className="text-xl font-medium"
								data-testid="subscription-header"
							>
								Subscription
							</h6>
							<Separator className="mt-3" />
						</div>

						<div className="flex flex-col gap-3 w-full">
							<div className="text-lg text-[var(--light-gray)] flex gap-2">
								<p>Member Since: </p>
								<div className="flex flex-col">
									<span
										className="text-accent-foreground font-medium"
										data-testid="member-since"
									>
										{memberSince}
									</span>

									<span
										className="text-accent-foreground text-sm"
										data-testid="member-since-pattern"
									>
										MM/DD/YY
									</span>
								</div>
							</div>

							<div className="flex flex-col items-start  gap-2 justify-between">
								<p className="text-lg text-[var(--light-gray)]">
									Subscription:{" "}
									<span
										className={cn(
											"text-xl font-bold uppercase mr-2",
											subscriptionTextColor,
										)}
										data-testid="user-roles"
									>
										{stripeRoles?.join(", ")}
									</span>
									{subscriptionWillCancel && (
										<span className="text-error-light">(Canceled)</span>
									)}
								</p>
								{isUserPremium && (
									<>
										<div className="text-lg text-[var(--light-gray)] flex gap-2">
											<p>
												{!subscriptionWillCancel
													? "Next invoice date:"
													: "The Premium plan expires on:"}{" "}
											</p>
											<div className="flex flex-col">
												<span className="text-accent-foreground font-medium">
													{nextPaymentDate}
												</span>
												<span className="text-accent-foreground text-sm">
													MM/DD/YY
												</span>
											</div>
										</div>

										<p className="text-lg text-[var(--light-gray)]">
											Subscription price:{" "}
											{formatValueCurrency(
												typeof userData.licencePrice === "number"
													? userData.licencePrice / 100
													: undefined,
												"N/A",
											)}
										</p>
										<p className="text-lg text-[var(--light-gray)]">
											Tax:{" "}
											{userData.taxPercentage !== undefined
												? `${userData.taxPercentage}%`
												: "N/A"}
										</p>
									</>
								)}
							</div>

							{isUserPremium && (
								<>
									<Link
										to={billingUrl || ""}
										data-testid="billing-link"
										className="text-[16px] text-gold mt-4 w-fit h-fit hover:text-foreground text-bold"
									>
										Manage billing
									</Link>
									{errorFetchBillingUrl && (
										<p className="text-red-500 text-lg">
											Unable to load subscription management. Please try again
											later or contact <EmailLink email={SUPPORT_EMAIL} />
										</p>
									)}
								</>
							)}

							{isUserPremium && (
								<>
									<Button
										data-testid="change-plan-link"
										className={BUTTON_STYLE}
										onClick={() => setShowPricingTable((prev) => !prev)}
									>
										Change my plan
									</Button>

									<div>
										<Button
											data-testid="cancel-subscription"
											className={cn(BUTTON_STYLE, "mb-4")}
											onClick={() => setOpenModal(true)}
											disabled={Boolean(subscriptionWillCancel)}
										>
											Cancel my subscription
										</Button>

										<p
											className="text-md text-[var(--light-gray)]"
											data-testid="cancel-subscription-text"
										>
											To delete your login, contact us at{" "}
											<EmailLink
												email={SUPPORT_EMAIL}
												className="text-[var(--light-gray)]"
											/>
										</p>
									</div>
								</>
							)}
						</div>
					</section>
				</div>
			</div>

			{showPricingTable && (
				<div className="w-full h-auto" ref={pricingRef} id="pricing">
					<PricingTable
						setSubscriptionWillCancel={setSubscriptionWillCancel}
						subscriptionWillCancel={subscriptionWillCancel}
					/>
				</div>
			)}

			<div className="flex flex-col gap-10 w-full  px-5 sm:px-20 md:px-40 py-5  cursor-not-allowed">
				<div className="flex flex-col gap-10 w-full max-w-3xl">
					<section
						className="flex flex-col gap-5"
						data-testid="portfolio-section"
					>
						<div>
							<h6 className="text-xl font-medium">Portfolio</h6>
							<Separator className="mt-3" />
						</div>

						<div className="flex flex-col gap-7 w-full relative">
							<div className="absolute top-0 left-0 w-full h-full bg-[var(--background)] z-10 opacity-70 flex items-center justify-center font-bold text-2xl text-foreground">
								<span>Coming soon!</span>
							</div>

							<Button variant="dropdown" className="text-[16px] w-fit">
								Connect my Brokerage account
							</Button>

							<p className="text-lg text-[var(--light-gray)]">
								Brokerage Account
							</p>

							<p className="text-lg text-[var(--light-gray)]">
								Status:{" "}
								<span className="text-accent-foreground font-bold uppercase !text-[var(--success-light)]">
									Active
								</span>
							</p>
						</div>
					</section>
				</div>
			</div>

			{openModal && (
				<RenderDialogForm
					openModal={openModal}
					setOpenModal={setOpenModal}
					subscriptionWillCancel={subscriptionWillCancel}
					setSubscriptionWillCancel={setSubscriptionWillCancel}
				/>
			)}
		</div>
	);
};
